import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInstruments } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PriceChart, SparklineChart } from '../components/TradingViewChart';
import { 
  Search, 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  LineChart,
  CandlestickChart,
  Clock,
  Loader2,
  Star,
  StarOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChartsPage = () => {
  const { user, token } = useAuth();
  const { instruments } = useInstruments();
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [chartType, setChartType] = useState('area');
  const [timeframe, setTimeframe] = useState('1M');
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [watchlist, setWatchlist] = useState(['AAPL', 'MSFT', 'BTC', 'ETH', 'GLD']);
  const [dataSource, setDataSource] = useState({ source: 'generated', isReal: false });

  const selectedInstrument = instruments.find(i => i.symbol === selectedSymbol);

  useEffect(() => {
    if (selectedSymbol) {
      fetchPriceHistory();
    }
  }, [selectedSymbol, timeframe]);

  const fetchPriceHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/instruments/${selectedSymbol}/history?timeframe=${timeframe}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        const data = await res.json();
        // Transform data for chart
        const chartData = (data.history || []).map(point => ({
          time: point.timestamp?.split('T')[0] || point.date,
          value: point.price || point.close,
          open: point.open || point.price,
          high: point.high || point.price,
          low: point.low || point.price,
          close: point.close || point.price,
          volume: point.volume
        }));
        setPriceHistory(chartData);
        setDataSource({
          source: data.source || 'unknown',
          isReal: data.is_real_data || false
        });
      } else {
        // Generate mock data for demo
        const mockData = generateMockPriceHistory(selectedInstrument?.current_price_vd || 100, timeframe);
        setPriceHistory(mockData);
        setDataSource({ source: 'generated', isReal: false });
      }
    } catch (err) {
      console.error('Error fetching price history:', err);
      // Generate mock data for demo
      const mockData = generateMockPriceHistory(selectedInstrument?.current_price_vd || 100, timeframe);
      setPriceHistory(mockData);
      setDataSource({ source: 'generated', isReal: false });
    } finally {
      setLoading(false);
    }
  };

  const generateMockPriceHistory = (currentPrice, tf) => {
    const days = tf === '1W' ? 7 : tf === '1M' ? 30 : tf === '3M' ? 90 : tf === '1Y' ? 365 : 30;
    const data = [];
    let price = currentPrice * 0.9; // Start 10% lower
    
    const today = new Date();
    for (let i = days; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Random walk with slight upward bias
      const change = (Math.random() - 0.48) * (price * 0.03);
      price = Math.max(price + change, price * 0.5);
      
      const open = price * (1 + (Math.random() - 0.5) * 0.02);
      const close = price;
      const high = Math.max(open, close) * (1 + Math.random() * 0.015);
      const low = Math.min(open, close) * (1 - Math.random() * 0.015);
      
      data.push({
        time: date.toISOString().split('T')[0],
        value: close,
        open,
        high,
        low,
        close,
        volume: Math.floor(Math.random() * 10000000) + 1000000
      });
    }
    
    // Ensure last price matches current price
    if (data.length > 0) {
      data[data.length - 1].value = currentPrice;
      data[data.length - 1].close = currentPrice;
    }
    
    return data;
  };

  const filteredInstruments = instruments.filter(i => 
    i.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleWatchlist = (symbol) => {
    if (watchlist.includes(symbol)) {
      setWatchlist(watchlist.filter(s => s !== symbol));
    } else {
      setWatchlist([...watchlist, symbol]);
    }
  };

  const priceChange = priceHistory.length > 1 
    ? ((priceHistory[priceHistory.length - 1]?.value - priceHistory[0]?.value) / priceHistory[0]?.value * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="charts-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-emerald-400" />
              Advanced Charts
            </h1>
            <p className="text-slate-400 mt-1">
              TradingView-powered price analysis
            </p>
          </div>
          {!user?.is_pro && (
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              Upgrade to Pro for real-time data
            </Badge>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Instrument Selection */}
          <div className="lg:col-span-1 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search symbols..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-900 border-slate-800 text-white"
              />
            </div>

            {/* Watchlist */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-400" />
                  Watchlist
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {watchlist.map(symbol => {
                  const inst = instruments.find(i => i.symbol === symbol);
                  return (
                    <div
                      key={symbol}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedSymbol === symbol 
                          ? 'bg-emerald-500/20 border border-emerald-500/30' 
                          : 'hover:bg-slate-800'
                      }`}
                      onClick={() => setSelectedSymbol(symbol)}
                    >
                      <div>
                        <p className="font-medium text-white text-sm">{symbol}</p>
                        <p className="text-slate-400 text-xs">
                          ${inst?.current_price_vd?.toFixed(2) || '—'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWatchlist(symbol);
                        }}
                      >
                        <StarOff className="h-3 w-3 text-yellow-400" />
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Instrument List */}
            <Card className="bg-slate-900 border-slate-800 max-h-96 overflow-y-auto">
              <CardHeader className="pb-2 sticky top-0 bg-slate-900 z-10">
                <CardTitle className="text-white text-sm">All Instruments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {filteredInstruments.slice(0, 50).map(inst => (
                  <div
                    key={inst.symbol}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedSymbol === inst.symbol 
                        ? 'bg-emerald-500/20 border border-emerald-500/30' 
                        : 'hover:bg-slate-800'
                    }`}
                    onClick={() => setSelectedSymbol(inst.symbol)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm">{inst.symbol}</p>
                      <p className="text-slate-400 text-xs truncate">{inst.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm">${inst.current_price_vd?.toFixed(2)}</p>
                      <Badge variant="outline" className="text-xs border-slate-700">
                        {inst.type}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Chart Area */}
          <div className="lg:col-span-3 space-y-4">
            {/* Chart Controls */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {/* Symbol Info */}
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-white">{selectedSymbol}</h2>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => toggleWatchlist(selectedSymbol)}
                        >
                          {watchlist.includes(selectedSymbol) ? (
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          ) : (
                            <Star className="h-4 w-4 text-slate-400" />
                          )}
                        </Button>
                      </div>
                      <p className="text-slate-400 text-sm">{selectedInstrument?.name}</p>
                    </div>
                    <div className="border-l border-slate-700 pl-4">
                      <p className="text-2xl font-bold text-white">
                        ${selectedInstrument?.current_price_vd?.toFixed(2) || '—'}
                      </p>
                      <div className={`flex items-center gap-1 ${priceChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {priceChange >= 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        <span>{priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%</span>
                        <span className="text-slate-400 text-sm">({timeframe})</span>
                      </div>
                    </div>
                    {/* Data Source Badge */}
                    <div className="border-l border-slate-700 pl-4">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${dataSource.isReal 
                          ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' 
                          : 'border-amber-500/50 text-amber-400 bg-amber-500/10'}`}
                      >
                        {dataSource.isReal ? (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            {dataSource.source === 'coingecko' ? 'CoinGecko' : 
                             dataSource.source === 'yahoo' ? 'Yahoo Finance' : 'Live'} Data
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            Simulated
                          </>
                        )}
                      </Badge>
                    </div>
                  </div>

                  {/* Chart Controls */}
                  <div className="flex items-center gap-2">
                    {/* Chart Type */}
                    <div className="flex bg-slate-800 rounded-lg p-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 px-3 ${chartType === 'area' ? 'bg-slate-700' : ''}`}
                        onClick={() => setChartType('area')}
                        title="Area Chart"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 px-3 ${chartType === 'line' ? 'bg-slate-700' : ''}`}
                        onClick={() => setChartType('line')}
                        title="Line Chart"
                      >
                        <LineChart className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 px-3 ${chartType === 'candlestick' ? 'bg-slate-700' : ''}`}
                        onClick={() => setChartType('candlestick')}
                        title="Candlestick Chart"
                      >
                        <CandlestickChart className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Timeframe */}
                    <Select value={timeframe} onValueChange={setTimeframe}>
                      <SelectTrigger className="w-24 bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="1W">1 Week</SelectItem>
                        <SelectItem value="1M">1 Month</SelectItem>
                        <SelectItem value="3M">3 Months</SelectItem>
                        <SelectItem value="1Y">1 Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Main Chart */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                {loading ? (
                  <div className="h-[400px] flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                  </div>
                ) : priceHistory.length > 0 ? (
                  <PriceChart
                    data={priceHistory}
                    symbol={selectedSymbol}
                    chartType={chartType}
                    height={400}
                    showVolume={chartType === 'candlestick'}
                    theme="dark"
                  />
                ) : (
                  <div className="h-[400px] flex items-center justify-center text-slate-400">
                    No price data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instrument Details */}
            {selectedInstrument && (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-slate-400 text-sm">Type</p>
                      <p className="text-white font-medium capitalize">{selectedInstrument.type}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Current Price</p>
                      <p className="text-white font-medium">${selectedInstrument.current_price_vd?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Week High</p>
                      <p className="text-white font-medium">
                        ${selectedInstrument.week_high?.toFixed(2) || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Week Low</p>
                      <p className="text-white font-medium">
                        ${selectedInstrument.week_low?.toFixed(2) || '—'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartsPage;
